/**
 * DC.ONE mobile floating UI fixes.
 * - Keeps the product enquiry WhatsApp icon correction.
 * - Moves WAPlus above the product sticky ATC.
 * - Moves WAPlus above the mini-cart recap while the drawer is open.
 * - Keeps the mobile mini-cart inside the ACTUAL visible viewport.
 */
(() => {
  const styleId = 'dc-mobile-floating-ui-fixes';
  let syncQueued = false;
  let cartObserver = null;
  let stickyObserver = null;
  let cartResizeObserver = null;
  let stickyResizeObserver = null;
  let observedCart = null;
  let observedSticky = null;

  function installStyles() {
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media screen and (max-width: 768px) {
        #mini-cart.mini-cart {
          height: var(--dc-mini-cart-visible-height, 100dvh) !important;
          max-height: var(--dc-mini-cart-visible-height, 100dvh) !important;
        }

        #mini-cart .mini-cart__inner {
          height: 100% !important;
          max-height: 100% !important;
          min-height: 0 !important;
        }

        body.template-product.dc-sticky-atc-active:not(.dc-mini-cart-open) #wa-widget-send-button {
          margin-bottom: var(--dc-sticky-atc-offset, 78px) !important;
        }

        body.template-product.dc-sticky-atc-active:not(.dc-mini-cart-open) .wa-chat-box,
        body.template-product.dc-sticky-atc-active:not(.dc-mini-cart-open) .wa-chat-bubble {
          bottom: var(--dc-sticky-atc-offset, 78px) !important;
        }

        body.dc-mini-cart-open #wa-widget-send-button {
          margin-bottom: var(--dc-mini-cart-recap-offset, 150px) !important;
        }

        body.dc-mini-cart-open .wa-chat-box,
        body.dc-mini-cart-open .wa-chat-bubble {
          bottom: var(--dc-mini-cart-recap-offset, 150px) !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function isMiniCartOpen(cart) {
    if (!cart) return false;
    if (cart.getAttribute('aria-hidden') === 'false') return true;
    if (cart.classList.contains('is-visible') || cart.classList.contains('open')) return true;
    return false;
  }

  function syncMiniCartViewport(cart) {
    if (!cart || !window.matchMedia('(max-width: 768px)').matches) {
      document.body.style.removeProperty('--dc-mini-cart-visible-height');
      return;
    }

    const viewport = window.visualViewport;
    const viewportTop = viewport ? viewport.offsetTop : 0;
    const viewportHeight = viewport ? viewport.height : window.innerHeight;
    const visibleBottom = viewportTop + viewportHeight;
    const cartRect = cart.getBoundingClientRect();
    const cartTop = Math.max(cartRect.top, viewportTop);
    const availableHeight = Math.max(320, Math.floor(visibleBottom - cartTop));

    document.body.style.setProperty('--dc-mini-cart-visible-height', `${availableHeight}px`);
  }

  function syncState() {
    syncQueued = false;

    const cart = document.querySelector('#mini-cart.mini-cart');
    syncMiniCartViewport(cart);

    const recap = cart && cart.querySelector('.mini-cart__recap');
    const cartOpen = isMiniCartOpen(cart);

    document.body.classList.toggle('dc-mini-cart-open', cartOpen);

    if (cartOpen && recap) {
      const recapOffset = Math.ceil(recap.getBoundingClientRect().height + 10);
      document.body.style.setProperty('--dc-mini-cart-recap-offset', `${recapOffset}px`);
    }

    const sticky = document.querySelector('.dc-sticky-atc');
    const stickyActive = !!sticky && sticky.classList.contains('is-visible');

    document.body.classList.toggle('dc-sticky-atc-active', stickyActive);

    if (sticky) {
      const stickyOffset = Math.ceil(sticky.getBoundingClientRect().height + 10);
      document.body.style.setProperty('--dc-sticky-atc-offset', `${stickyOffset}px`);
    }

    bindObservers(cart, sticky, recap);
  }

  function scheduleSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(syncState);
  }

  function bindObservers(cart, sticky, recap) {
    if (cart !== observedCart) {
      if (cartObserver) cartObserver.disconnect();
      if (cartResizeObserver) cartResizeObserver.disconnect();
      observedCart = cart;

      if (cart) {
        cartObserver = new MutationObserver(scheduleSync);
        cartObserver.observe(cart, {
          attributes: true,
          attributeFilter: ['aria-hidden', 'class'],
          childList: true,
          subtree: true
        });

        if ('ResizeObserver' in window && recap) {
          cartResizeObserver = new ResizeObserver(scheduleSync);
          cartResizeObserver.observe(recap);
        }
      }
    }

    if (sticky !== observedSticky) {
      if (stickyObserver) stickyObserver.disconnect();
      if (stickyResizeObserver) stickyResizeObserver.disconnect();
      observedSticky = sticky;

      if (sticky) {
        stickyObserver = new MutationObserver(scheduleSync);
        stickyObserver.observe(sticky, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });

        if ('ResizeObserver' in window) {
          stickyResizeObserver = new ResizeObserver(scheduleSync);
          stickyResizeObserver.observe(sticky);
        }
      }
    }
  }

  const enquiryIconSelector = '.dc-whatsapp-enquiry-button__icon';
  const brandIcon = '<svg class="dc-whatsapp-enquiry-button__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false" data-dc-whatsapp-brand-icon="true"><path fill="currentColor" d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.93 7.93 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.25a6.56 6.56 0 0 1-1.007-3.492c0-3.638 2.961-6.599 6.603-6.599a6.57 6.57 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.965 6.599-6.6 6.599m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.514.646-.627.775-.116.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.101-1.372-.116-.198-.013-.306.087-.404.09-.088.197-.232.296-.346.1-.116.133-.198.198-.33.065-.134.033-.248-.016-.347-.05-.099-.445-1.076-.61-1.47-.161-.389-.323-.335-.445-.34-.116-.007-.248-.007-.38-.007a.73.73 0 0 0-.529.248c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.132 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.905.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.462.164-.858.116-.943-.05-.084-.182-.132-.38-.23"/></svg>';

  function replaceEnquiryIcon() {
    if (!document.body.classList.contains('template-product')) return;
    const icon = document.querySelector(enquiryIconSelector);
    if (!icon || icon.dataset.dcWhatsappBrandIcon === 'true') return;
    icon.outerHTML = brandIcon;
  }

  function initialise() {
    installStyles();
    replaceEnquiryIcon();
    scheduleSync();

    document.addEventListener('cart:rerendered', scheduleSync, true);
    document.documentElement.addEventListener('cart:refresh', scheduleSync, true);
    document.addEventListener('shopify:section:load', scheduleSync, true);
    window.addEventListener('resize', scheduleSync, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleSync, { passive: true });
      window.visualViewport.addEventListener('scroll', scheduleSync, { passive: true });
    }

    const bodyObserver = new MutationObserver(() => {
      replaceEnquiryIcon();
      scheduleSync();
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
